import "./RequestSidebar.scss";

import React from "react";
import { Button, Offcanvas } from "react-bootstrap";

import type { IApplication } from "../../hooks/reactQueryHooks/useApplications";
import type { IData } from "../../types";
import {
  formatMilliseconds,
  formatNumber,
} from "../../utils/NumbersFormatting";
import {
  CacheColumn,
  CacheStatusViewColumn,
  CostColumn,
  DateColumn,
  ModelColumn,
  StatusColumn,
  TypeColumn,
} from "../CustomizedTable/Columns";
import { ArrowIcon } from "../icons";
import { RequestSidebarLoader } from "./Loader";
import { RequestSidebarItem } from "./RequestSidebarItem";
import { RequestSidebarQuestion } from "./RequestSidebarQuestion";
import { RequestSidebarResponse } from "./RequestSidebarResponce";

interface RequestSidebarProps {
  sidebarData: IData;
  isFirstItem: boolean;
  isLastItem: boolean;
  isLoading: boolean;
  applications: Array<IApplication>;
  onNextClick: () => void;
  onPrevClick: () => void;
  onClose: () => void;
}

const RequestSidebar: React.FC<RequestSidebarProps> = ({
  sidebarData,
  isFirstItem,
  isLastItem,
  isLoading,
  applications,
  onClose,
  onNextClick,
  onPrevClick,
}) => {
  const {
    response,
    request,
    prices,
    isFree,
    cacheItemId,
    applicationId,
    cacheType,
  } = sidebarData;

  const application = applications.find(
    application => application.id === applicationId,
  );

  return (
    <Offcanvas
      show
      onHide={onClose}
      backdrop
      placement="end"
      className="request-sidebar"
    >
      <Offcanvas.Header closeButton className="request-sidebar-header">
        <h3>Request details</h3>
        <Button
          variant="link"
          className="prev-request"
          disabled={isFirstItem}
          onClick={onPrevClick}
          data-testid="prev-request"
        >
          <ArrowIcon />
        </Button>
        <Button
          variant="link"
          className="next-request"
          onClick={onNextClick}
          disabled={isLastItem}
          data-testid="next-request"
        >
          <ArrowIcon />
        </Button>
      </Offcanvas.Header>
      <Offcanvas.Body className="request-sidebar-body">
        <RequestSidebarItem title="Project">
          {application?.name}
        </RequestSidebarItem>
        <RequestSidebarItem title="Status">
          <StatusColumn statusCode={response.statusCode} />
        </RequestSidebarItem>
        <RequestSidebarItem title="Date">
          <DateColumn type="long" date={request.timestamp} />
        </RequestSidebarItem>
        <RequestSidebarItem title="Source">
          <CacheStatusViewColumn response={response} cacheType={cacheType} />
        </RequestSidebarItem>
        <RequestSidebarItem title="Cache item">
          {cacheItemId ? (
            <CacheColumn
              id={cacheItemId}
              applicationId={applicationId}
              headers={request.headers}
            />
          ) : (
            "n/a"
          )}
        </RequestSidebarItem>
        <RequestSidebarItem title="Prompt type">
          {response.promptType ? (
            <TypeColumn type={response.promptType} />
          ) : (
            "n/a"
          )}
        </RequestSidebarItem>
        <RequestSidebarItem title="Model">
          <ModelColumn
            modelType={response.model}
            modelVersion={response.modelVersion}
          />
        </RequestSidebarItem>
        <RequestSidebarItem title="Cost">
          <CostColumn prices={prices} isFree={isFree} />
        </RequestSidebarItem>
        <RequestSidebarItem title="Tokens">
          {`Prompt: ${formatNumber(
            response.promptTokens,
          )} / Completion: ${formatNumber(response.completionTokens)} `}
        </RequestSidebarItem>
        <RequestSidebarItem title="Latency">
          {formatMilliseconds(
            Math.round(Math.max(response.totalTime * 1000, 1)),
          )}
        </RequestSidebarItem>
        <RequestSidebarItem title="Endpoint">{request.path}</RequestSidebarItem>

        <RequestSidebarQuestion
          messages={request.body.messages}
          promptContext={
            response.promptType === "rag" ? response.promptContext.data : null
          }
        />
        <RequestSidebarResponse response={response.body.choices} />
      </Offcanvas.Body>

      {isLoading && <RequestSidebarLoader />}
    </Offcanvas>
  );
};

export { RequestSidebar };
